import {
  capitalizeTerm,
  defaultForgeMineCopy,
  type ForgeBoxStatusValue,
  formatAmountWithUnit,
  type MyForgeBox,
} from "@loot-os/api-contract";
import type {
  ForgeConfirmContent,
  ForgeMineBoxesContent,
  ForgeMineContent,
  ForgeMineRow,
} from "@loot-os/design-system/templates/forge/forgeContent";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useCurrency } from "../../../lib/currency.js";
import {
  forgeErrorMessage,
  useClaimForgeEarnings,
  useSetForgeBoxStatus,
} from "../../../lib/forge.js";
import { myForgeQueryOptions } from "../../../lib/queries.js";
import { useShopper } from "../../../lib/shopper.js";
import { useTerminology } from "../../../lib/terminology.js";
import { showToast } from "../../../lib/toasts.js";

/** A status change the shopper confirms first: deleting is for good. */
interface PendingChange {
  box: MyForgeBox;
  status: ForgeBoxStatusValue;
}

/**
 * GET /v1/me/forge: the shopper's own forge boxes, what they have earned, and the claim. Archive
 * and Delete ask first; Unarchive applies at once. Saves and refusals are toasts.
 */
export function useForgeMineContent(): ForgeMineContent {
  const { sessionToken } = useShopper();
  const terms = useTerminology();
  const { box } = terms;
  const Box = capitalizeTerm(box.singular);
  const copy = defaultForgeMineCopy(terms);
  const currency = useCurrency();
  const query = useQuery({ ...myForgeQueryOptions(), enabled: !!sessionToken });
  const claim = useClaimForgeEarnings();
  const setStatus = useSetForgeBoxStatus();
  const [confirm, setConfirm] = useState<PendingChange | null>(null);

  const earnings = query.data?.earningsCents ?? 0;
  const boxes = query.data?.boxes ?? [];

  /** Saves a status change; rejects (after the toast) when it was refused. */
  async function applyChange(change: PendingChange): Promise<void> {
    try {
      await setStatus.mutateAsync({ boxId: change.box.id, status: change.status });
    } catch (err) {
      showToast({ kind: "error", title: forgeErrorMessage(err, { terms, currency }) });
      throw err;
    }
    setConfirm(null);
    showToast({
      kind: "success",
      title:
        change.status === "deleted"
          ? `${Box} deleted.`
          : change.status === "archived"
            ? `${Box} archived.`
            : `${Box} is live again.`,
    });
  }

  const toRow = (mine: MyForgeBox): ForgeMineRow => {
    const live = mine.status === "active";
    const actions: ForgeMineRow["actions"] = [
      ...(live ? [{ label: copy.boxes.actions.edit, href: `/forge/${mine.id}/edit` }] : []),
      ...(live
        ? [
            {
              label: copy.boxes.actions.archive,
              onClick: () => setConfirm({ box: mine, status: "archived" }),
            },
          ]
        : mine.status === "archived"
          ? [
              {
                label: copy.boxes.actions.unarchive,
                disabled: setStatus.isPending,
                onClick: () => void applyChange({ box: mine, status: "active" }).catch(() => {}),
              },
            ]
          : []),
      {
        label: copy.boxes.actions.delete,
        tone: "danger" as const,
        onClick: () => setConfirm({ box: mine, status: "deleted" }),
      },
    ];
    return {
      id: mine.id,
      name: mine.name,
      image: mine.imageUrl,
      href: live ? `/boxes/${mine.id}` : null,
      status: { label: copy.boxes.status[mine.status] ?? mine.status, live },
      facts: [
        { label: copy.boxes.facts.price, cents: mine.priceCents },
        {
          label: copy.boxes.facts.opens(mine.openedCount),
          text: mine.openedCount.toLocaleString(),
        },
        { label: copy.boxes.facts.cut, text: `${mine.creatorCutPercent}%` },
        { label: copy.boxes.facts.earned, cents: mine.lifetimeEarningsCents },
      ],
      actions,
    };
  };

  // Only Archive and Delete ask first; Unarchive applies at once.
  const confirmKind = confirm?.status === "deleted" ? "delete" : "archive";
  const confirmContent: ForgeConfirmContent | null = confirm && {
    title: copy.boxes.confirm[confirmKind].title(confirm.box.name),
    description: copy.boxes.confirm[confirmKind].description,
    confirmLabel: copy.boxes.actions[confirmKind],
    cancelLabel: copy.boxes.confirm.cancel,
    tone: confirm.status === "deleted" ? "danger" : "default",
    onConfirm: () => applyChange(confirm),
    onCancel: () => setConfirm(null),
  };

  let state: ForgeMineBoxesContent["state"];
  if (query.isError) {
    state = {
      status: "error",
      message: forgeErrorMessage(query.error, { terms, currency }),
    };
  } else if (query.isPending) state = { status: "loading" };
  else if (boxes.length === 0) {
    state = {
      status: "empty",
      message: copy.boxes.empty,
      action: null,
    };
  } else state = { status: "ready", rows: boxes.map(toRow) };

  return {
    heading: {
      trail: [
        { label: copy.heading.home, href: "/" },
        { label: copy.heading.forge, href: "/forge" },
      ],
      current: copy.heading.title,
      title: copy.heading.title,
      intro: copy.heading.intro,
      back: { label: copy.heading.forge, href: "/forge" },
      action: { label: copy.heading.create, href: "/forge/create", emphasis: "primary" },
    },
    earnings: {
      title: copy.earnings.title,
      description: copy.earnings.description,
      currency,
      cents: query.isPending && !query.isError ? null : earnings,
      claim: {
        label: claim.isPending ? copy.earnings.claiming : copy.earnings.claim,
        disabled: earnings <= 0 || claim.isPending,
        busy: claim.isPending,
        onClaim: () =>
          claim.mutate(undefined, {
            onSuccess: (res) =>
              showToast({
                kind: "success",
                title: `Claimed ${formatAmountWithUnit(res.amountCents, currency)}.`,
              }),
            onError: (err) =>
              showToast({ kind: "error", title: forgeErrorMessage(err, { terms, currency }) }),
          }),
      },
    },
    boxes: {
      title: copy.boxes.title,
      currency,
      state,
      confirm: confirmContent,
    },
  };
}
